import React from 'react';
import { Score } from '../../types/score';

interface ScoreHeroSectionProps {
  score: Score;
  siteUrl: string;
}

const ScoreHeroSection: React.FC<ScoreHeroSectionProps> = ({ score, siteUrl }) => {
  return (
    <div className="relative overflow-hidden mb-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-800 p-8 shadow-xl">
      {/* Effets décoratifs en arrière-plan */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
        <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full blur-3xl transform -translate-x-20 -translate-y-20"></div>
        <div className="absolute bottom-0 right-0 w-60 h-60 bg-blue-300 rounded-full blur-3xl transform translate-x-20 translate-y-20"></div>
      </div>
      
      <div className="relative z-10">
        <h1 className="text-4xl font-extrabold text-white mb-3 animate-fadeSlideUp" style={{animationDelay: '100ms'}}>
          Votre score digital basé sur vos réponses
        </h1>
        <p className="text-blue-100 mb-8 max-w-xl animate-fadeSlideUp" style={{animationDelay: '200ms'}}>
          Analyse basée sur vos réponses au questionnaire d'évaluation de la maturité digitale.
          Découvrez les opportunités pour renforcer votre présence en ligne.
        </p>
        
        {/* Layout en deux colonnes : Score Global + Détail par Catégorie */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 animate-fadeSlideUp" style={{animationDelay: '300ms'}}>
          
          {/* Score principal avec effet visuel */}
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 blur-md transform scale-105"></div>
            <div className="relative bg-white rounded-full p-1">
              <div className="flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full h-40 w-40 text-center">
                <div>
                  <div className="text-5xl font-bold">{score.overall}%</div>
                  <div className="text-sm font-medium mt-1">Score Global</div>
                </div>
              </div>
            </div>
          </div>

          {/* Détail par Catégorie à droite du score */}
          <div className="flex-1 bg-white/90 backdrop-blur-sm rounded-xl shadow-md overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border-b border-blue-100">
              <h2 className="text-xl font-bold text-blue-800">Détail par Catégorie</h2>
            </div>
            
            <div className="grid grid-cols-3 gap-0 divide-x divide-blue-100">
              {score.categories.map((category, index) => {
                // Couleurs différentes pour chaque catégorie
                const colors = [
                  { from: 'from-blue-500', to: 'to-blue-600', light: 'bg-blue-50', text: 'text-blue-800' },
                  { from: 'from-indigo-500', to: 'to-indigo-600', light: 'bg-indigo-50', text: 'text-indigo-800' },
                  { from: 'from-purple-500', to: 'to-purple-600', light: 'bg-purple-50', text: 'text-purple-800' },
                ];
                const color = colors[index % colors.length];
                
                return (
                  <div key={index} className="p-4 flex flex-col items-center hover:bg-blue-50/50 transition-colors duration-150">
                    <div className="mb-2 text-sm font-semibold text-gray-700 text-center leading-tight">{category.name}</div>
                    <div className="relative">
                      <div className={`absolute inset-0 rounded-full ${color.light} blur-sm transform scale-105`}></div>
                      <div className="relative bg-white rounded-full p-1">
                        <div className={`flex items-center justify-center bg-gradient-to-br ${color.from} ${color.to} text-white rounded-full h-16 w-16 text-center`}>
                          <div className="text-lg font-bold">{category.score}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreHeroSection;
